import React, { useEffect, useState } from "react";
import { api, fmt, CURRENCIES, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Trash2, Settings, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Expenses() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [cats, setCats] = useState([]);
    const [open, setOpen] = useState(false);
    const [catOpen, setCatOpen] = useState(false);
    const [newCat, setNewCat] = useState("");
    const [form, setForm] = useState({
        category: "",
        amount: "",
        currency: "TRY",
        exchange_rate_try: "1",
        description: "",
    });

    async function loadAll() {
        const [e, c] = await Promise.all([
            api.get("/expenses"),
            api.get("/expense-categories"),
        ]);
        setItems(e.data);
        setCats(c.data);
        if (!form.category && c.data.length) {
            setForm((f) => ({ ...f, category: c.data[0].name }));
        }
    }
    useEffect(() => {
        loadAll();
        // eslint-disable-next-line
    }, []);

    async function submit(e) {
        e.preventDefault();
        try {
            await api.post("/expenses", {
                ...form,
                amount: Number(form.amount),
                exchange_rate_try:
                    form.currency === "TRY"
                        ? 1
                        : Number(form.exchange_rate_try) || 0,
            });
            toast.success("Gider kaydedildi");
            setOpen(false);
            setForm({
                category: cats[0]?.name || "",
                amount: "",
                currency: "TRY",
                exchange_rate_try: "1",
                description: "",
            });
            loadAll();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }
    async function del(id) {
        await api.delete(`/expenses/${id}`);
        loadAll();
    }
    async function addCategory(e) {
        e.preventDefault();
        try {
            await api.post("/expense-categories", { name: newCat });
            toast.success("Kategori eklendi");
            setNewCat("");
            loadAll();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }
    async function delCategory(id) {
        try {
            await api.delete(`/expense-categories/${id}`);
            loadAll();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    return (
        <div data-testid="expenses-page">
            <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900">
                        Günlük Giderler
                    </h1>
                    <p className="text-slate-600 mt-1">
                        Kliniğinizin gider kalemlerini kategori bazlı takip edin
                    </p>
                </div>
                <div className="flex gap-2">
                    {user?.role === "patron" && (
                        <Dialog open={catOpen} onOpenChange={setCatOpen}>
                            <DialogTrigger asChild>
                                <Button
                                    variant="outline"
                                    data-testid="manage-categories-btn"
                                >
                                    <Settings className="w-4 h-4 mr-1" />
                                    Kategoriler
                                </Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle>
                                        Gider Kategorilerini Yönet
                                    </DialogTitle>
                                </DialogHeader>
                                <form
                                    onSubmit={addCategory}
                                    className="flex gap-2"
                                >
                                    <Input
                                        placeholder="Yeni kategori adı"
                                        value={newCat}
                                        onChange={(e) =>
                                            setNewCat(e.target.value)
                                        }
                                        data-testid="new-category-input"
                                    />
                                    <Button
                                        type="submit"
                                        className="dk-btn-primary"
                                        data-testid="add-category-btn"
                                    >
                                        Ekle
                                    </Button>
                                </form>
                                <div className="mt-4 max-h-72 overflow-y-auto space-y-1.5">
                                    {cats.map((c) => (
                                        <div
                                            key={c.id}
                                            className="flex items-center justify-between px-3 py-2 rounded-md border border-slate-200 bg-slate-50"
                                        >
                                            <span className="text-sm font-medium text-slate-700">
                                                {c.name}
                                            </span>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 w-7 p-0 text-slate-400 hover:text-rose-600"
                                                onClick={() =>
                                                    delCategory(c.id)
                                                }
                                                data-testid={`del-category-${c.id}`}
                                            >
                                                <X className="w-3.5 h-3.5" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </DialogContent>
                        </Dialog>
                    )}
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button
                                className="dk-btn-primary rounded-full"
                                data-testid="add-expense-btn"
                            >
                                <Plus className="w-4 h-4 mr-1" /> Yeni Gider
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Yeni Gider Kaydı</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={submit} className="space-y-3">
                                <div>
                                    <Label>Kategori</Label>
                                    <Select
                                        value={form.category}
                                        onValueChange={(v) =>
                                            setForm({ ...form, category: v })
                                        }
                                    >
                                        <SelectTrigger data-testid="expense-category-select">
                                            <SelectValue placeholder="Kategori seçin" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {cats.map((c) => (
                                                <SelectItem
                                                    key={c.id}
                                                    value={c.name}
                                                >
                                                    {c.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label>Tutar</Label>
                                        <Input
                                            required
                                            type="number"
                                            step="0.01"
                                            value={form.amount}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    amount: e.target.value,
                                                })
                                            }
                                            data-testid="expense-amount-input"
                                        />
                                    </div>
                                    <div>
                                        <Label>Para Birimi</Label>
                                        <Select
                                            value={form.currency}
                                            onValueChange={(v) =>
                                                setForm({
                                                    ...form,
                                                    currency: v,
                                                    exchange_rate_try:
                                                        v === "TRY" ? "1" : "",
                                                })
                                            }
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {CURRENCIES.map((c) => (
                                                    <SelectItem
                                                        key={c.code}
                                                        value={c.code}
                                                    >
                                                        {c.symbol} {c.code}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                {form.currency !== "TRY" && (
                                    <div>
                                        <Label>
                                            1 {form.currency} = ? TL (aylık
                                            rapor için)
                                        </Label>
                                        <Input
                                            type="number"
                                            step="0.0001"
                                            required
                                            value={form.exchange_rate_try}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    exchange_rate_try:
                                                        e.target.value,
                                                })
                                            }
                                            data-testid="expense-rate-input"
                                        />
                                    </div>
                                )}
                                <div>
                                    <Label>Açıklama</Label>
                                    <Input
                                        value={form.description}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                description: e.target.value,
                                            })
                                        }
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    className="dk-btn-primary w-full"
                                    data-testid="expense-submit-btn"
                                >
                                    Kaydet
                                </Button>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            <Card>
                <CardContent className="p-0">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-600">
                                <th className="px-6 py-3">Tarih</th>
                                <th className="px-6 py-3">Kategori</th>
                                <th className="px-6 py-3">Açıklama</th>
                                <th className="px-6 py-3 text-right">Tutar</th>
                                <th className="px-6 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((e) => (
                                <tr
                                    key={e.id}
                                    className="border-b hover:bg-slate-50"
                                >
                                    <td className="px-6 py-4 text-sm text-slate-500">
                                        {(e.date || e.created_at)?.slice(
                                            0,
                                            10,
                                        )}
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="text-xs font-semibold px-2 py-1 rounded-full bg-slate-100 text-slate-700">
                                            {e.category}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-slate-600">
                                        {e.description || "-"}
                                    </td>
                                    <td className="px-6 py-4 text-right mono font-semibold text-rose-700">
                                        -{fmt(e.amount, e.currency)}
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => del(e.id)}
                                            data-testid={`expense-delete-${e.id}`}
                                        >
                                            <Trash2 className="w-4 h-4 text-slate-400" />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                            {items.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={5}
                                        className="text-center text-slate-500 py-12"
                                    >
                                        Henüz gider kaydı yok.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </CardContent>
            </Card>
        </div>
    );
}
